import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Paper,
  Stack,
  Title,
  Text,
  TextInput,
  NumberInput,
  Input,
  Select,
  MultiSelect,
  Avatar,
  Group,
  Button,
  Collapse,
  Tooltip,
  ActionIcon,
  Loader,
  Alert,
  UnstyledButton,
  Box,
  Modal,
  Switch,
  FileButton,
  MantineProvider,
} from "@mantine/core";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { Node as TiptapNode, useEditor } from "@tiptap/react";
import { Plugin } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import {
  IconPencil,
  IconAlertCircle,
  IconTrash,
  IconPlus,
  IconChevronRight,
  IconSearch,
} from "@tabler/icons-react";
import { theme } from "../../styles/theme";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import PageContent from "../shared/PageContent/PageContent";
import SubNav from "../shared/Navigation/SubNav";
import { WELCOME_SURVEY_TABS } from "./welcomeSurveyTabs";
import InfoVerificationPage from "../Verification/InfoVerificationPage";
import WelcomeSurveyEmailPreview from "./WelcomeSurveyEmailPreview";
import { buildPreviewData } from "./surveyPreviewData";
import "./WelcomeSurveySettingsPage.css";

// Every company has a survey template: the default coverage the survey falls
// back on, which isn't one of the office's FieldRoutes services. It is stored
// as coverage rows keyed on this name with no fr_service_id, and the settings
// page always shows it, selected, so Survey Setup opens on something.
const SURVEY_TEMPLATE_NAME = "Survey Template";
const SURVEY_TEMPLATE_ID = "survey-template";

function emptySurveyTemplate() {
  return {
    serviceId: SURVEY_TEMPLATE_ID,
    isTemplate: true,
    includedBugs: [],
    excludedBugs: [],
    cancellationFee: "",
  };
}

// Puts the template at the head of the list, adding an empty one when the
// company has never saved it. Called on every load, so the page is never
// without it.
function withSurveyTemplate(serviceBugs) {
  const rest = serviceBugs.filter(entry => !entry.isTemplate);
  const existing = serviceBugs.find(entry => entry.isTemplate);
  return [existing ?? emptySurveyTemplate(), ...rest];
}

const EMPTY_SETTINGS = {
  inboundPhone: "",
  contractText: "",
  emailHeadline: "",
  emailBody: "",
  headerImageUrl: "",
  emailHeroImageUrl: "",
  branchEmail: "",
  branchName: "",
  branchPhone: "",
  branchAddress: "",
  managerName: "",
  managerPhotoUrl: "",
};

const EMPTY_REP = {
  firstName: "",
  lastName: "",
  employeeId: "",
  phone: "",
  // fieldroutes_offices ids the rep is assigned to.
  officeIds: [],
};

// Reps arrive from FieldRoutes with the name split across two columns.
function repName(entry) {
  return `${entry?.first_name || ""} ${entry?.last_name || ""}`.trim();
}

// Map the API's welcome survey settings payload onto the form's state shape.
function mapSettings(data) {
  if (!data) return EMPTY_SETTINGS;
  return {
    inboundPhone: data.welcome_survey_inbound_phone ?? "",
    contractText: data.welcome_survey_contract_text ?? "",
    // Hero copy on the invitation email; blank means the template's defaults.
    emailHeadline: data.welcome_survey_email_headline ?? "",
    emailBody: data.welcome_survey_email_body ?? "",
    headerImageUrl: data.welcome_survey_header_image_url ?? "",
    emailHeroImageUrl: data.welcome_survey_email_hero_image_url ?? "",
    branchEmail: data.welcome_survey_branch_email ?? "",
    branchName: data.welcome_survey_branch_name ?? "",
    branchPhone: data.welcome_survey_branch_phone ?? "",
    branchAddress: data.welcome_survey_branch_address ?? "",
    managerName: data.welcome_survey_manager_name ?? "",
    managerPhotoUrl: data.welcome_survey_manager_photo_url ?? "",
  };
}

// Each editable image has its own upload endpoint and the response field that
// carries the resulting URL back onto the form state.
const IMAGE_UPLOADS = {
  headerImageUrl: {
    endpoint: "/v1/welcome-survey/image/header",
    responseKey: "header_image_url",
    removeFlag: "remove_header_image",
  },
  emailHeroImageUrl: {
    endpoint: "/v1/welcome-survey/image/email-hero",
    responseKey: "email_hero_image_url",
    removeFlag: "remove_main_image",
  },
  managerPhotoUrl: {
    endpoint: "/v1/welcome-survey/image/manager-photo",
    responseKey: "manager_photo_url",
    removeFlag: "remove_manager_image",
  },
};

// Pull the image URL out of an upload response, tolerating different shapes:
// the specific settings key, common url-ish keys, or the first URL-looking
// string value in the object.
function pickImageUrl(obj, responseKey) {
  if (!obj || typeof obj !== "object") return "";
  const candidates = [
    responseKey,
    "url",
    "image_url",
    "imageUrl",
    "location",
    "Location",
  ];
  for (const key of candidates) {
    if (typeof obj[key] === "string" && obj[key]) return obj[key];
  }
  const found = Object.values(obj).find(
    value => typeof value === "string" && /^https?:\/\//i.test(value)
  );
  return found || "";
}

// Parse the settings response's `pest_coverage` map (keyed by fr_service_id)
// into editable service rows, plus the insect/service objects it references so
// labels and images can render without the bug-settings option catalog.
function parsePestCoverage(pestCoverage) {
  const serviceBugs = [];
  const insectsById = {};
  const servicesById = {};

  if (pestCoverage && typeof pestCoverage === "object") {
    Object.entries(pestCoverage).forEach(([serviceId, records]) => {
      if (!Array.isArray(records)) return;
      const includedBugs = [];
      const excludedBugs = [];
      let contractText = "";

      records.forEach(record => {
        const insectValue = insectKeyFor(record) ?? String(record.fr_insect_id);
        if (record.coverage_type === "not_included") {
          excludedBugs.push(insectValue);
        } else {
          includedBugs.push(insectValue);
        }
        // Per-service contract terms ride along on the coverage records.
        if (!contractText && record.contract_terms) {
          contractText = record.contract_terms;
        }
        insectsById[insectValue] = {
          fr_insect_id: record.fr_insect_id ?? null,
          custom_insect_id: record.custom_insect_id ?? null,
          is_custom: record.custom_insect_id != null,
          name: record.pest_name,
          image_url: record.image_url,
        };
        if (record.fr_service_id != null) {
          servicesById[String(record.fr_service_id)] = {
            fr_service_id: record.fr_service_id,
            name: record.service_type,
          };
        }
      });

      serviceBugs.push({
        serviceId: String(serviceId),
        includedBugs,
        excludedBugs,
        contractText,
      });
    });
  }

  return {
    serviceBugs,
    insects: Object.values(insectsById),
    services: Object.values(servicesById),
  };
}

// Parse the settings response's `services` array into editable service rows,
// plus the insect/service objects referenced so labels/images render before
// the bug-settings option catalog loads.
function parseServices(services) {
  const serviceBugs = [];
  const insectsById = {};
  const servicesById = {};

  if (Array.isArray(services)) {
    services.forEach(service => {
      const serviceId = String(service.fr_service_id);

      // Record an insect referenced by a service and return its string id.
      const collect = record => {
        const insectValue = insectKeyFor(record) ?? String(record.fr_insect_id);
        insectsById[insectValue] = {
          fr_insect_id: record.fr_insect_id ?? null,
          custom_insect_id: record.custom_insect_id ?? null,
          is_custom: record.custom_insect_id != null,
          name: record.name,
          image_url: record.image_url,
        };
        return insectValue;
      };

      const includedBugs = (
        Array.isArray(service.included) ? service.included : []
      ).map(collect);
      const excludedBugs = (
        Array.isArray(service.not_included) ? service.not_included : []
      ).map(collect);

      const isTemplate = service.fr_service_id == null;
      if (!isTemplate) {
        servicesById[serviceId] = {
          fr_service_id: service.fr_service_id,
          name: service.fr_service_type,
        };
      }

      serviceBugs.push({
        serviceId: isTemplate ? SURVEY_TEMPLATE_ID : serviceId,
        isTemplate,
        includedBugs,
        excludedBugs,
        cancellationFee: service.cancellation_fee ?? "",
      });
    });
  }

  return {
    serviceBugs,
    insects: Object.values(insectsById),
    services: Object.values(servicesById),
  };
}

// What a saved row snapshots for an insect: the FieldRoutes name and photo,
// not the company's own. Overrides are laid over rows when they are read, so
// the snapshot must stay the plain catalog value — otherwise resetting a bug
// to FieldRoutes would leave its old custom name behind in every saved list.
function insectSnapshot(insect) {
  return {
    fr_insect_id: insect.fr_insect_id ?? null,
    custom_insect_id: insect.custom_insect_id ?? null,
    name: insect.default_name ?? insect.name,
    image_url: insect.default_image_url ?? insect.image_url ?? null,
  };
}

// Map the Service Bug Settings rows onto the save payload. Each service carries
// its id, name, and the insect snapshots for included / not-included bugs.
function toBugPayload(serviceBugs, insectByValue, serviceNameByValue) {
  return serviceBugs
    .filter(entry => entry.serviceId)
    .map(entry => ({
      // The template has no FieldRoutes service; the API keys it on the name.
      fr_service_id: entry.isTemplate ? null : Number(entry.serviceId),
      name: entry.isTemplate
        ? SURVEY_TEMPLATE_NAME
        : serviceNameByValue[entry.serviceId] || "",
      cancellation_fee:
        entry.cancellationFee === "" || entry.cancellationFee == null
          ? null
          : Number(entry.cancellationFee),
      included: entry.includedBugs
        .map(v => insectByValue[v])
        .filter(Boolean)
        .map(insectSnapshot),
      not_included: entry.excludedBugs
        .map(v => insectByValue[v])
        .filter(Boolean)
        .map(insectSnapshot),
    }));
}

// Service frequency as the design's chip reads it — a word where the interval
// is a familiar one, the raw cadence otherwise. Null when FieldRoutes hasn't
// given the service a frequency, so the chip is left off rather than guessed.
function frequencyLabel(days) {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n <= 10) return "Weekly";
  if (n <= 35) return "Monthly";
  if (n <= 70) return "Bi-monthly";
  if (n <= 100) return "Quarterly";
  if (n <= 200) return "Semi-annual";
  if (n <= 400) return "Annual";
  return `Every ${n} days`;
}

// Month labels for the seasonal-window pickers. Values are the 1-based month
// numbers the API stores, as strings for Mantine's Select.
const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTH_OPTIONS = MONTH_LABELS.map((label, index) => ({
  value: String(index + 1),
  label,
}));

// Target pests are picked by FieldRoutes insect id. Entries saved without one
// (hand-authored rows) are keyed by name behind this prefix so they still show
// up selected and survive a round trip instead of being silently dropped.
const NAMED_PEST_PREFIX = "name:";

// Company-added bugs have no FieldRoutes id; they are keyed by their own id
// behind this prefix wherever a FieldRoutes insect would be keyed by its id.
const CUSTOM_INSECT_PREFIX = "custom:";

// The picker value for an insect object from the API: the FieldRoutes id, or
// the prefixed custom id. Null when it carries neither.
function insectKeyFor(insect) {
  if (!insect) return null;
  if (insect.fr_insect_id != null) return String(insect.fr_insect_id);
  if (insect.custom_insect_id != null) {
    return `${CUSTOM_INSECT_PREFIX}${insect.custom_insect_id}`;
  }
  return null;
}

// The live preview renders an iPhone 16 at its real point size — a 393 x 852pt
// screen inside a 12px bezel — so the survey lays out exactly as it does on the
// phone. The whole shell is then scaled to fit the panel; scaling changes how
// big it looks, not how anything inside it wraps.
const PHONE_OUTER_WIDTH = 417;
const PHONE_OUTER_HEIGHT = 876;
// Matches .survey-live-preview's sticky offset, so the fit is measured against
// where the panel sits once it's pinned.
const PHONE_STICKY_TOP = 16;
const PHONE_MIN_SCALE = 0.5;

// The live preview's service picker is keyed by FieldRoutes service id. A
// service type that only has seasonal windows configured has no id to key on
// (windows are stored by name), so those entries sit behind this prefix.
const SEASON_ONLY_PREFIX = "season:";

// Parse GET /service-seasons — a map of FieldRoutes service id to its windows —
// into editable rows, plus the insects referenced so labels and images render
// before the bug-settings catalog loads. The payload carries no service-type
// name, so `serviceType` is left blank here and resolved from the catalog
// (`serviceNameByValue[serviceId]`) wherever a row is displayed or saved.
function parseServiceSeasons(data) {
  const rows = [];
  const insectsById = {};

  if (data && typeof data === "object") {
    Object.entries(data).forEach(([serviceIdKey, seasons]) => {
      if (!Array.isArray(seasons)) return;
      // The service id every window in this group was saved under. The map is
      // keyed by it, and each window echoes it as `fr_service_id`; prefer the
      // window field, falling back to the key.
      const frServiceId =
        seasons.find(season => season.fr_service_id != null)?.fr_service_id ??
        serviceIdKey;
      rows.push({
        serviceType: "",
        serviceId: frServiceId != null ? String(frServiceId) : null,
        seasons: seasons.map(season => ({
          name: season.name ?? "",
          startMonth:
            season.start_month != null ? String(season.start_month) : null,
          endMonth: season.end_month != null ? String(season.end_month) : null,
          pests: (Array.isArray(season.target_pests)
            ? season.target_pests
            : []
          ).map(pest => {
            const value = insectKeyFor(pest);
            if (value == null) {
              return `${NAMED_PEST_PREFIX}${pest.name}`;
            }
            insectsById[value] = {
              fr_insect_id: pest.fr_insect_id ?? null,
              custom_insect_id: pest.custom_insect_id ?? null,
              is_custom: pest.custom_insect_id != null,
              name: pest.name,
              image_url: pest.image_url,
            };
            return value;
          }),
        })),
      });
    });
  }

  return { rows, insects: Object.values(insectsById) };
}

// Map one editable service-season row onto the PUT payload. The endpoint
// replaces that service type's whole window list, so every window goes up,
// keyed on the FieldRoutes service id. Rows carry the id they were configured
// under; windows saved before it was stored fall back to `serviceIdByName`,
// which resolves the service-type name to its numeric id.
function toSeasonPayload(row, insectByValue, serviceIdByName) {
  const serviceType = row.serviceType.trim();
  const frServiceId =
    row.serviceId ?? serviceIdByName?.[serviceType.toLowerCase()];
  return {
    fr_service_id: Number(frServiceId),
    service_type: serviceType,
    seasons: row.seasons.map(season => ({
      name: season.name.trim(),
      start_month: Number(season.startMonth),
      end_month: Number(season.endMonth),
      pests: season.pests
        .map(value => {
          if (value.startsWith(NAMED_PEST_PREFIX)) {
            return { pest_name: value.slice(NAMED_PEST_PREFIX.length) };
          }
          const insect = insectByValue[value];
          if (!insect) return null;
          const snapshot = insectSnapshot(insect);
          return {
            pest_name: snapshot.name,
            ...(snapshot.fr_insect_id != null
              ? { fr_insect_id: snapshot.fr_insect_id }
              : {}),
            ...(snapshot.custom_insect_id != null
              ? { custom_insect_id: snapshot.custom_insect_id }
              : {}),
            ...(snapshot.image_url ? { image_url: snapshot.image_url } : {}),
          };
        })
        .filter(Boolean),
    })),
  };
}

// Everything wrong with a service's visit windows, as sentences the admin can
// act on. Empty means the windows are safe to save. A service with no windows
// at all is fine — it just has no seasonal labels on its schedule.
function validateSeasonRows(rows) {
  const problems = [];

  rows.forEach(row => {
    const label = row.serviceType?.trim() || "This service";

    row.seasons.forEach((season, seasonIndex) => {
      const windowLabel = season.name?.trim() || `window ${seasonIndex + 1}`;
      if (!season.name?.trim()) {
        problems.push(`${label}: ${windowLabel} needs a visit name.`);
      }
      if (!season.startMonth || !season.endMonth) {
        problems.push(`${label}: ${windowLabel} needs a start and end month.`);
      }
    });
  });

  return [...new Set(problems)];
}

// Basic WYSIWYG editor for the email contract text. Stores its value as HTML.
// Shows a formatting toolbar while editable and renders read-only otherwise.
// One of the two coverage boxes — what the plan targets, and what it
// explicitly doesn't. Empty until bugs are picked from the index below it.
function BugBox({
  label,
  tone,
  values,
  nameFor,
  imageFor,
  editable,
  onRemove,
}) {
  return (
    <div className="ws-bug-box">
      <span
        className={`ws-bug-box-label${tone === "target" ? " ws-bug-box-label-target" : ""}`}
      >
        {label}
      </span>
      {values.length === 0 ? (
        <span className="ws-bug-box-empty" />
      ) : (
        <div className="ws-bug-grid">
          {values.map(value => (
            <div className="ws-bug-tile" key={value}>
              <Avatar src={imageFor[value]} size={38} radius="xl">
                {nameFor(value)?.charAt(0)}
              </Avatar>
              <span className="ws-bug-name">{nameFor(value)}</span>
              {editable && (
                <span className="ws-bug-actions">
                  <button
                    type="button"
                    className="ws-bug-pick"
                    aria-label={`Remove ${nameFor(value)}`}
                    onClick={() => onRemove(value)}
                  >
                    ✕
                  </button>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// The cancellation fee placeholder in the contract copy. Stored and sent to
// the API as this literal — the server and the customer page both substitute
// it by plain string replacement — but shown in the editor as a chip that
// reads "Cancellation Fee", so nobody has to type or spot the token.
const CANCELLATION_FEE_TOKEN = "{{CANCELLATION_FEE}}";
const CANCELLATION_FEE_TOKEN_RE = /\{\{\s*CANCELLATION_FEE\s*\}\}/gi;
// The chip's HTML form inside the editor. Body-less: the label is drawn by
// the node itself, so the stored text never carries it.
const CANCELLATION_FEE_CHIP_HTML = '<span data-cancellation-fee=""></span>';
const CANCELLATION_FEE_CHIP_RE =
  /<span[^>]*\bdata-cancellation-fee\b[^>]*>[\s\S]*?<\/span>/gi;

// Editor node for the placeholder: an inline atom, so it moves as one piece,
// can't be typed into, and is dragged around the text like a chip.
const CancellationFeeToken = TiptapNode.create({
  name: "cancellationFeeToken",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,
  parseHTML() {
    return [{ tag: "span[data-cancellation-fee]" }];
  },
  renderHTML() {
    return [
      "span",
      {
        "data-cancellation-fee": "",
        class: "ws-fee-token",
        contenteditable: "false",
      },
      "Cancellation Fee",
    ];
  },
  renderText() {
    return CANCELLATION_FEE_TOKEN;
  },
  addProseMirrorPlugins() {
    const type = this.type;
    const count = doc => {
      let n = 0;
      doc.descendants(node => {
        if (node.type === type) n += 1;
      });
      return n;
    };
    return [
      new Plugin({
        // The chip is part of the text, always: a change that would take the
        // last one out is dropped. Dragging it is a single replace that keeps
        // one, so that still goes through; so does deleting a duplicate.
        filterTransaction: (tr, state) =>
          !tr.docChanged || count(state.doc) === 0 || count(tr.doc) > 0,
      }),
    ];
  },
});

// Editor HTML with the chip guaranteed present: terms saved without the
// placeholder get it appended to their last line (or as their only line).
function withFeeChip(html) {
  if (CANCELLATION_FEE_CHIP_RE.test(html)) return html;
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const lines = tpl.content.querySelectorAll("p, h1, h2, h3, li");
  const last = lines[lines.length - 1];
  const chip = document.createElement("span");
  chip.setAttribute("data-cancellation-fee", "");
  if (last) {
    if (last.textContent.trim()) last.append(" ");
    last.append(chip);
  } else {
    const line = document.createElement("p");
    line.append(chip);
    tpl.content.append(line);
  }
  return tpl.innerHTML;
}

// Stored HTML → what the editor shows: chips for tokens, and one chip present.
function contractEditorHtml(value) {
  return withFeeChip(contractTokensToChips(value));
}

// Stored HTML → editor HTML: each literal token becomes a chip node.
function contractTokensToChips(html) {
  return (html || "").replace(
    CANCELLATION_FEE_TOKEN_RE,
    CANCELLATION_FEE_CHIP_HTML
  );
}

// Editor HTML → stored HTML: each chip goes back to the literal token, so what
// the API receives is exactly what it always has.
function contractChipsToTokens(html) {
  return (html || "").replace(CANCELLATION_FEE_CHIP_RE, CANCELLATION_FEE_TOKEN);
}

function ContractTextEditor({ value, editable, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit, Link, CancellationFeeToken],
    content: contractEditorHtml(value),
    editable,
    onUpdate: ({ editor: instance }) =>
      onChange(contractChipsToTokens(instance.getHTML())),
  });

  // Keep the editor's editable state in sync with edit mode. Without the
  // second argument Tiptap emits an update here, which on Cancel handed the
  // discarded text back to the page just before the snapshot was restored,
  // so the revert was overwritten by what it was reverting.
  useEffect(() => {
    editor?.setEditable(editable, false);
  }, [editable, editor]);

  // Reflect external value changes (initial load, cancel/revert) without
  // clobbering in-progress typing. Passing `false` skips firing onUpdate.
  // Compared in stored form, since that's what onUpdate hands back.
  useEffect(() => {
    if (!editor) return;
    const next = value || "";
    if (next !== contractChipsToTokens(editor.getHTML())) {
      editor.commands.setContent(contractEditorHtml(next), false);
    }
  }, [value, editor]);

  return (
    <Input.Wrapper>
      <RichTextEditor editor={editor} mt={4}>
        {editable && (
          <RichTextEditor.Toolbar sticky>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Strikethrough />
              <RichTextEditor.ClearFormatting />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.H1 />
              <RichTextEditor.H2 />
              <RichTextEditor.H3 />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.BulletList />
              <RichTextEditor.OrderedList />
              <RichTextEditor.Blockquote />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Link />
              <RichTextEditor.Unlink />
            </RichTextEditor.ControlsGroup>
          </RichTextEditor.Toolbar>
        )}
        <RichTextEditor.Content />
      </RichTextEditor>
    </Input.Wrapper>
  );
}

function WelcomeSurveySettingsPage() {
  const { getVerifiedJWT, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [error, setError] = useState(null);
  // Email content section: collapsed by default, and edited straight off the
  // rendered email — a pencil per part, each saving on its own.
  const [emailSectionOpen, setEmailSectionOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(false);
  const [branchDraft, setBranchDraft] = useState({
    branchName: "",
    branchAddress: "",
    branchPhone: "",
    branchEmail: "",
  });
  const [editingManager, setEditingManager] = useState(false);
  const [managerDraft, setManagerDraft] = useState("");
  const [editingHero, setEditingHero] = useState(false);
  const [heroDraft, setHeroDraft] = useState({ headline: "", body: "" });
  const [savingEmail, setSavingEmail] = useState(false);
  // Contract terms section (its own edit mode / save call).
  const [isEditingContract, setIsEditingContract] = useState(false);
  const [savingContract, setSavingContract] = useState(false);
  // Snapshot of the last-saved contract text, used to revert on cancel.
  const [contractSnapshot, setContractSnapshot] = useState("");
  const [isEditingBugs, setIsEditingBugs] = useState(false);
  const [bugSettings, setBugSettings] = useState(null);
  const [bugSettingsLoading, setBugSettingsLoading] = useState(false);
  // Each entry: { serviceId, includedBugs: string[], excludedBugs: string[] }.
  const [serviceBugs, setServiceBugs] = useState([]);
  // Snapshot of the last-saved rows, used to revert on cancel.
  const [serviceBugsSnapshot, setServiceBugsSnapshot] = useState([]);
  // Insect/service objects referenced by the saved pest coverage, so rows can
  // render labels/images before the bug-settings option catalog is loaded.
  const [coverageInsects, setCoverageInsects] = useState([]);
  const [coverageServices, setCoverageServices] = useState([]);
  // Whether the bug settings were changed since entering bug edit mode.
  const [bugsDirty, setBugsDirty] = useState(false);
  // Inline save error for bug settings: { message, conflicts: [{ service_type, bugs }] }.
  const [bugError, setBugError] = useState(null);
  // Index into serviceBugs of the service type being viewed, or null for the
  // list.
  const [selectedServiceIndex, setSelectedServiceIndex] = useState(null);
  // Live preview panel: the service type it renders (a service id, or a
  // SEASON_ONLY_PREFIX name), and a counter bumped to remount the survey so it
  // starts over with the boxes clear.
  const [previewServiceValue, setPreviewServiceValue] = useState(null);
  // How far the device mock is scaled down to fit the panel (1 = life size).
  const [phoneScale, setPhoneScale] = useState(1);
  const phoneStageRef = useRef(null);
  // Accent color for the preview. It lives on the company row rather than
  // company_configs, so it comes from the branding endpoint.
  const [branding, setBranding] = useState(null);
  // Visit windows (named month ranges + target bugs), edited inside the
  // service type they belong to.
  const [seasonsLoading, setSeasonsLoading] = useState(true);
  // Each row: { serviceId, serviceType, seasons: [{ name, startMonth,
  // endMonth, pests }] }.
  const [seasonRows, setSeasonRows] = useState([]);
  // Last-saved rows, used to revert on cancel and to spot service types the
  // admin removed (which need an explicit DELETE).
  const [seasonRowsSnapshot, setSeasonRowsSnapshot] = useState([]);
  // Insects referenced by saved seasons, so labels render before the catalog loads.
  const [seasonInsects, setSeasonInsects] = useState([]);
  // Inline error for the visit windows: { message, details: string[] }.
  const [seasonsError, setSeasonsError] = useState(null);

  // Reps: whose phone numbers may generate the welcome survey.
  const [whitelist, setWhitelist] = useState([]);
  const [whitelistLoading, setWhitelistLoading] = useState(true);
  // Add/edit modal: `whitelistEditing` is the entry being edited, or null when
  // adding a new one; the modal is open whenever `whitelistFormOpen` is true.
  const [whitelistFormOpen, setWhitelistFormOpen] = useState(false);
  const [whitelistEditing, setWhitelistEditing] = useState(null);
  const [whitelistDraft, setWhitelistDraft] = useState(EMPTY_REP);
  const [whitelistSaving, setWhitelistSaving] = useState(false);
  const [whitelistFormError, setWhitelistFormError] = useState("");
  // Delete confirmation modal: the entry pending deletion, or null.
  const [bugSearch, setBugSearch] = useState("");
  const [whitelistSearch, setWhitelistSearch] = useState("");
  const [whitelistDeleting, setWhitelistDeleting] = useState(null);
  // Bug edit modal (the pencil on a Bug Index tile): the company's own name
  // and photo for that FieldRoutes insect. Null when closed.
  const [bugEdit, setBugEdit] = useState(null);
  const [bugEditSaving, setBugEditSaving] = useState(false);
  const [bugEditUploading, setBugEditUploading] = useState(false);
  const [whitelistRemoving, setWhitelistRemoving] = useState(false);

  // Offices, read-only here: the Reps section shows which ones a rep belongs
  // to. They're added and synced on the FieldRoutes Setup page.
  const [offices, setOffices] = useState([]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint("/v2/welcome-survey/settings"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load welcome survey settings");
      }

      const result = await response.json();
      const data = result?.data ?? result;
      const mapped = mapSettings(data);
      setSettings(mapped);
      setContractSnapshot(mapped.contractText);

      // Prefill the Service Settings rows from the saved services (falling back
      // to the legacy pest_coverage shape if `services` is absent).
      const parsed = Array.isArray(data?.services)
        ? parseServices(data.services)
        : parsePestCoverage(data?.pest_coverage);
      const withTemplate = withSurveyTemplate(parsed.serviceBugs);
      setServiceBugs(withTemplate);
      setServiceBugsSnapshot(withTemplate);
      // The template leads the list, so Survey Setup opens on it rather than
      // on "Open a service type above to edit it".
      setSelectedServiceIndex(0);
      setCoverageInsects(parsed.insects);
      setCoverageServices(parsed.services);
    } catch (err) {
      setError(err.message || "Failed to load welcome survey settings");
    } finally {
      setLoading(false);
    }
  }, [getVerifiedJWT]);

  // Seasonal windows live behind their own endpoint, so they load alongside
  // the settings rather than as part of that payload.
  const loadSeasons = useCallback(async () => {
    setSeasonsLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint("/v2/welcome-survey/service-seasons"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to load service seasons");

      const result = await response.json();
      const parsed = parseServiceSeasons(result?.data ?? result);
      setSeasonRows(parsed.rows);
      setSeasonRowsSnapshot(parsed.rows);
      setSeasonInsects(parsed.insects);
    } catch (err) {
      setSeasonsError({
        message: err.message || "Failed to load service seasons",
        details: [],
      });
    } finally {
      setSeasonsLoading(false);
    }
  }, [getVerifiedJWT]);

  // Keep the latest toast reporter in a ref so the loaders below can stay
  // stable. `showError` gets a fresh identity every time a toast is shown, so
  // depending on it directly would recreate the loader and refire its mount
  // effect on every toast (e.g. a save success would trigger a spurious reload).
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  // The rep list lives behind its own endpoint.
  const loadWhitelist = useCallback(async () => {
    setWhitelistLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(getApiEndpoint("/v2/welcome-survey/reps"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!response.ok) throw new Error("Failed to load reps");

      const result = await response.json();
      const data = result?.data ?? result;
      setWhitelist(Array.isArray(data) ? data : []);
    } catch (err) {
      showErrorRef.current(err.message || "Failed to load reps");
    } finally {
      setWhitelistLoading(false);
    }
  }, [getVerifiedJWT]);

  const loadOffices = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(getApiEndpoint("/v1/fieldroutes/offices"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!response.ok) throw new Error("Failed to load offices");

      const result = await response.json();
      const data = result?.data ?? result;
      setOffices(Array.isArray(data) ? data : []);
    } catch (err) {
      showErrorRef.current(err.message || "Failed to load offices");
    }
  }, [getVerifiedJWT]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    loadSeasons();
  }, [loadSeasons]);

  useEffect(() => {
    loadWhitelist();
  }, [loadWhitelist]);

  useEffect(() => {
    loadOffices();
  }, [loadOffices]);

  // Accent color for the Service Types preview. Decorative, so a failure here
  // leaves the preview on the default theme color rather than erroring.
  useEffect(() => {
    let cancelled = false;

    const loadBranding = async () => {
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;
        const response = await fetch(
          getApiEndpoint("/v2/welcome-survey/branding"),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );
        if (!response.ok) return;
        const result = await response.json();
        if (!cancelled) setBranding(result?.data ?? result);
      } catch {
        // Preview styling only — leave the accent unset.
      }
    };

    loadBranding();
    return () => {
      cancelled = true;
    };
  }, [getVerifiedJWT]);

  // Fit the life-size device mock to the panel: scale it down by whichever is
  // tighter, the column's width or the room left under the panel's heading and
  // picker. Never scaled up past 1 — the phone is shown at most life size.
  useEffect(() => {
    const stage = phoneStageRef.current;
    if (!stage) return undefined;

    const panel = stage.closest(".survey-live-preview");

    const measure = () => {
      // Measured as an offset inside the sticky panel rather than from the
      // viewport, so the fit doesn't change as the page scrolls.
      const offsetInPanel = panel
        ? stage.getBoundingClientRect().top - panel.getBoundingClientRect().top
        : 0;
      const room =
        window.innerHeight -
        PHONE_STICKY_TOP -
        offsetInPanel -
        PHONE_STICKY_TOP;
      const fit = Math.min(
        1,
        stage.clientWidth / PHONE_OUTER_WIDTH,
        room / PHONE_OUTER_HEIGHT
      );
      setPhoneScale(Math.max(PHONE_MIN_SCALE, Number.isFinite(fit) ? fit : 1));
    };

    measure();

    // The column's width and the panel's own height (the picker wraps, errors
    // appear) both move the goalposts.
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    if (panel) observer.observe(panel);
    window.addEventListener("resize", measure);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [loading, error]);

  // Load the FieldRoutes option catalog (insects + service types) once. Both
  // the Service Settings and Service Seasons editors pick from it.
  const ensureBugSettings = useCallback(async () => {
    // Only fetch the option data once (or if a previous load failed).
    if (bugSettings) return;
    setBugSettingsLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint("/v2/welcome-survey/bug-settings"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load service settings");
      }

      const result = await response.json();
      setBugSettings(result?.data ?? result);
    } catch (err) {
      showError(err.message || "Failed to load service settings");
    } finally {
      setBugSettingsLoading(false);
    }
  }, [bugSettings, getVerifiedJWT, showError]);

  // Enter edit mode for bug settings and fetch the data needed to edit them.
  // The Bug Index is on screen whenever a service type is open, not just in
  // edit mode, so the catalog has to be there from the start — it used to be
  // fetched only when the pencil was clicked, which left the index empty.
  useEffect(() => {
    ensureBugSettings();
  }, [ensureBugSettings]);

  const handleEditBugs = async () => {
    setIsEditingBugs(true);
    setBugsDirty(false);
    await ensureBugSettings();
  };

  // Service Bug Settings editing rows.
  const addServiceBug = () => {
    setBugsDirty(true);
    // Open the new row straight away — there's nothing to see in the list yet.
    setSelectedServiceIndex(serviceBugs.length);
    setServiceBugs(prev => [
      ...prev,
      {
        serviceId: null,
        includedBugs: [],
        excludedBugs: [],
        cancellationFee: "",
      },
    ]);
  };
  const updateServiceBug = (index, patch) => {
    setBugsDirty(true);
    setServiceBugs(prev =>
      prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry))
    );
  };

  // Visit windows, edited inside the service type they belong to. Rows are
  // keyed by FieldRoutes service id, and a service gets its row the first time
  // a window is added to it.
  const seasonRowFor = serviceId =>
    serviceId == null
      ? null
      : (seasonRows.find(row => row.serviceId === serviceId) ?? null);

  const seasonsFor = serviceId => seasonRowFor(serviceId)?.seasons ?? [];

  // Apply `updater` to one service's window list, creating the row if this is
  // its first window.
  const updateSeasonsFor = (serviceId, updater) => {
    if (serviceId == null) return;
    setSeasonsError(null);
    setBugsDirty(true);
    setSeasonRows(prev => {
      const index = prev.findIndex(row => row.serviceId === serviceId);
      if (index === -1) {
        return [
          ...prev,
          {
            serviceId,
            serviceType: serviceNameByValue[serviceId] || "",
            seasons: updater([]),
          },
        ];
      }
      return prev.map((row, i) =>
        i === index
          ? {
              ...row,
              // Keep the stored name in step with the catalog, since the
              // survey matches windows to subscriptions by service.
              serviceType: serviceNameByValue[serviceId] || row.serviceType,
              seasons: updater(row.seasons),
            }
          : row
      );
    });
  };

  // Windows aren't added freely: the subscription decides how many visits a
  // plan runs, and each slot is one of them. Filling slot N creates the empty
  // windows before it too, so a window always sits at the visit it belongs to
  // rather than at the end of the list.
  const fillSeasonSlot = (serviceId, slotIndex) =>
    updateSeasonsFor(serviceId, seasons => {
      const next = [...seasons];
      while (next.length <= slotIndex) {
        next.push({ name: "", startMonth: null, endMonth: null, pests: [] });
      }
      return next;
    });

  const updateSeason = (serviceId, seasonIndex, patch) =>
    updateSeasonsFor(serviceId, seasons =>
      seasons.map((season, i) =>
        i === seasonIndex ? { ...season, ...patch } : season
      )
    );

  const removeSeason = (serviceId, seasonIndex) =>
    updateSeasonsFor(serviceId, seasons =>
      seasons.filter((_, i) => i !== seasonIndex)
    );

  // Dropdown/multiselect option sources: the saved pest coverage (for display)
  // merged with the bug-settings catalog (loaded on edit). Bug-settings entries
  // come last so the fuller catalog (e.g. images) wins for the same id.
  const serviceNameByValue = [
    ...coverageServices,
    ...(bugSettings?.service_types ?? []),
  ].reduce((acc, s) => {
    acc[String(s.fr_service_id)] = s.name;
    return acc;
  }, {});
  const serviceTypeOptions = Object.entries(serviceNameByValue).map(
    ([value, name]) => ({ value, label: `${value} - ${name}` })
  );
  // Reverse lookup: service-type name (lowercased) -> fr_service_id. Season rows
  // are keyed by name, but the seasons save call must send the numeric id.
  const serviceIdByName = [
    ...coverageServices,
    ...(bugSettings?.service_types ?? []),
  ].reduce((acc, s) => {
    if (s?.name) acc[s.name.trim().toLowerCase()] = s.fr_service_id;
    return acc;
  }, {});
  const insectByValue = [
    ...coverageInsects,
    ...seasonInsects,
    ...(bugSettings?.insects ?? []),
  ].reduce((acc, i) => {
    const key = insectKeyFor(i);
    if (key != null) acc[key] = i;
    return acc;
  }, {});
  const insectOptions = Object.entries(insectByValue).map(
    ([value, insect]) => ({ value, label: insect.name })
  );
  const insectImageByValue = Object.entries(insectByValue).reduce(
    (acc, [value, i]) => {
      acc[value] = i.image_url;
      return acc;
    },
    {}
  );
  // Target-pest options: the insect catalog, plus any pest saved without a
  // FieldRoutes id so existing entries stay selected instead of dropping.
  const namedPestOptions = [
    ...new Set(
      seasonRows.flatMap(row =>
        row.seasons.flatMap(season =>
          season.pests.filter(value => value.startsWith(NAMED_PEST_PREFIX))
        )
      )
    ),
  ].map(value => ({
    value,
    label: value.slice(NAMED_PEST_PREFIX.length),
  }));
  const seasonPestOptions = [...insectOptions, ...namedPestOptions];

  // Insect name for a stored value, falling back to the value itself so a
  // legacy name-keyed entry still reads as something.
  const insectNameFor = value =>
    insectOptions.find(option => option.value === value)?.label ??
    (value?.startsWith?.(NAMED_PEST_PREFIX)
      ? value.slice(NAMED_PEST_PREFIX.length)
      : value);

  const bugQuery = bugSearch.trim().toLowerCase();
  const visibleInsects = bugQuery
    ? insectOptions.filter(option =>
        option.label.toLowerCase().includes(bugQuery)
      )
    : insectOptions;

  // A bug is a target, or not covered, or neither — never both. Picking the
  // side it is already on takes it off both lists, so the buttons toggle.
  const setBugSide = (value, side) => {
    const service = serviceBugs[selectedServiceIndex];
    if (!service) return;
    const wasIn = service.includedBugs.includes(value);
    const wasOut = service.excludedBugs.includes(value);
    const included = service.includedBugs.filter(v => v !== value);
    const excluded = service.excludedBugs.filter(v => v !== value);
    if (side === "in" && !wasIn) included.push(value);
    if (side === "out" && !wasOut) excluded.push(value);
    updateServiceBug(selectedServiceIndex, {
      includedBugs: included,
      excludedBugs: excluded,
    });
  };

  // Render each insect option as a circular image with the name beside it.
  const renderInsectOption = ({ option }) => (
    <Group gap="sm" wrap="nowrap">
      <Avatar src={insectImageByValue[option.value]} size={24} radius="xl">
        {option.label?.charAt(0)}
      </Avatar>
      <span>{option.label}</span>
    </Group>
  );

  // Resolve a picker value (insect id, or a name-keyed legacy entry) back to
  // the { name, image_url, fr_insect_id } shape the survey renders.
  const pestItemFor = value => {
    if (value.startsWith(NAMED_PEST_PREFIX)) {
      return {
        name: value.slice(NAMED_PEST_PREFIX.length),
        image_url: null,
        fr_insect_id: null,
      };
    }
    const insect = insectByValue[value];
    return insect
      ? {
          name: insect.name,
          image_url: insect.image_url ?? null,
          fr_insect_id: insect.fr_insect_id ?? null,
        }
      : null;
  };

  // The visit windows currently in the editor for a service, converted into the
  // shape the preview builder expects. Reads the live rows rather than the API
  // so unsaved window edits show up in the preview too. Matched on the service
  // id when there is one, falling back to the name for windows saved before a
  // service had coverage configured.
  const previewSeasonsFor = (serviceTypeName, serviceId = null) => {
    const row =
      (serviceId
        ? seasonRows.find(candidate => candidate.serviceId === serviceId)
        : null) ??
      (serviceTypeName
        ? seasonRows.find(
            candidate =>
              candidate.serviceType &&
              candidate.serviceType.toLowerCase() ===
                serviceTypeName.toLowerCase()
          )
        : null);
    if (!row) return [];
    return row.seasons
      .filter(season => season.startMonth && season.endMonth)
      .map(season => ({
        name: season.name,
        start_month: Number(season.startMonth),
        end_month: Number(season.endMonth),
        target_pests: season.pests.map(pestItemFor).filter(Boolean),
      }));
  };

  const selectedService =
    selectedServiceIndex == null ? null : serviceBugs[selectedServiceIndex];

  // What FieldRoutes has on file for the open service, and the visit windows
  // configured against it.
  const selectedServiceSchedule = selectedService?.serviceId
    ? ((bugSettings?.service_types ?? []).find(
        option => String(option.fr_service_id) === selectedService.serviceId
      ) ?? null)
    : null;
  const selectedServiceSeasons = seasonsFor(selectedService?.serviceId ?? null);

  // How many visits the plan runs a year, as FieldRoutes has it on the service
  // type. Null when FieldRoutes has no frequency for it, which is common
  // enough that the label says so rather than showing a made-up number.
  const selectedVisitsPerYear =
    selectedServiceSchedule?.visits_per_year ?? null;

  // One slot per visit the plan runs, so a four-visit plan shows four cards
  // whether or not each has a window configured. If more windows exist than
  // the plan has visits, every window still gets a card rather than being
  // hidden behind the count.
  const visitSlots = Array.from(
    {
      length: Math.max(
        selectedVisitsPerYear ?? 0,
        selectedServiceSeasons.length
      ),
    },
    (_, index) => index
  );

  // The span the configured windows cover end to end, e.g. "Jan – Dec". Blank
  // until at least one window has both of its months set.
  const selectedWindowRange = (() => {
    const months = selectedServiceSeasons.flatMap(season =>
      [season.startMonth, season.endMonth]
        .map(Number)
        .filter(month => Number.isFinite(month) && month >= 1 && month <= 12)
    );
    if (months.length === 0) return "";
    const short = month => MONTH_LABELS[month - 1]?.slice(0, 3) ?? "";
    return `${short(Math.min(...months))} – ${short(Math.max(...months))}`;
  })();

  // Opening a service type in the editor points the preview at it, so the panel
  // shows whatever is being worked on.
  const openServiceId = selectedService?.serviceId ?? null;
  useEffect(() => {
    if (openServiceId) setPreviewServiceValue(openServiceId);
  }, [openServiceId]);

  // Service types the preview can be shown as: everything with coverage
  // configured, plus any service type that only has seasonal windows. The two
  // are configured separately and don't have to line up — a name set up on one
  // side only previews exactly the way the live survey renders it.
  const configuredServiceNames = new Set(
    serviceBugs
      .map(entry => serviceNameByValue[entry.serviceId])
      .filter(Boolean)
      .map(name => name.toLowerCase())
  );
  const previewServiceOptions = [
    ...serviceBugs
      .filter(entry => entry.serviceId)
      .map(entry => ({
        value: entry.serviceId,
        label:
          serviceNameByValue[entry.serviceId] || `Service ${entry.serviceId}`,
      })),
    ...[...new Set(seasonRows.map(row => row.serviceType).filter(Boolean))]
      .filter(name => !configuredServiceNames.has(name.toLowerCase()))
      .map(name => ({ value: `${SEASON_ONLY_PREFIX}${name}`, label: name })),
  ];

  // Fall back to the first option whenever the chosen one goes away (a service
  // removed, or its row not yet given a service).
  const previewValue =
    previewServiceValue &&
    previewServiceOptions.some(option => option.value === previewServiceValue)
      ? previewServiceValue
      : (previewServiceOptions[0]?.value ?? null);

  const previewEntry =
    previewValue && !previewValue.startsWith(SEASON_ONLY_PREFIX)
      ? serviceBugs.find(entry => entry.serviceId === previewValue)
      : null;
  const previewServiceName = previewEntry
    ? serviceNameByValue[previewEntry.serviceId] || ""
    : previewValue
      ? previewValue.slice(SEASON_ONLY_PREFIX.length)
      : "";

  // Open the preview on the step the section being edited feeds: the
  // subscription for service types, the schedule for seasons, the terms for the
  // contract text. Otherwise start at the top of the survey.
  const previewStep = isEditingContract
    ? 3
    : isEditingBugs || selectedServiceIndex != null
      ? 1
      : 0;

  // The customer-facing survey for the chosen service type, built from the
  // in-progress editor state so it tracks edits before they're saved.
  const previewData = buildPreviewData({
    serviceType: previewServiceName,
    includedPests: (previewEntry?.includedBugs ?? [])
      .map(pestItemFor)
      .filter(Boolean),
    notIncludedPests: (previewEntry?.excludedBugs ?? [])
      .map(pestItemFor)
      .filter(Boolean),
    seasons: previewSeasonsFor(previewServiceName, previewEntry?.serviceId),
    contractText: settings.contractText,
    headerImageUrl: settings.headerImageUrl,
    accentColor: branding?.accent_color,
    cancellationFee:
      previewEntry?.cancellationFee === "" ||
      previewEntry?.cancellationFee == null
        ? null
        : Number(previewEntry.cancellationFee),
  });

  // Save a slice of the email settings. Every key is optional server-side, so
  // each edit sends only what it changed.
  const saveSettingsPatch = async (payload, successMessage) => {
    setSavingEmail(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint("/v2/welcome-survey/settings"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to save welcome survey settings");
      }

      showSuccess(successMessage);
      return true;
    } catch (err) {
      showError(err.message || "Failed to save welcome survey settings");
      return false;
    } finally {
      setSavingEmail(false);
    }
  };

  // ── Bug overrides ──
  // A company can give any FieldRoutes catalog bug its own name and photo.
  // The API lays those over the catalog and every saved list, so once one
  // is set it shows everywhere the bug does; here the loaded copies of the
  // catalog are patched the same way so the page needn't refetch.

  const openBugEdit = value => {
    const insect = insectByValue[value];
    if (!insect) return;
    setBugEdit({
      value,
      isNew: false,
      isCustom: !!insect.is_custom,
      customId: insect.custom_insect_id ?? null,
      frInsectId: insect.fr_insect_id ?? null,
      name: insect.name ?? "",
      defaultName: insect.default_name ?? insect.name ?? "",
      imageUrl: insect.image_url ?? null,
      defaultImageUrl: insect.default_image_url ?? insect.image_url ?? null,
      isCustomized: !!insect.is_customized,
      pendingFile: null,
    });
  };

  // The "+" tile: a company-added bug. It is created on Save; a photo picked
  // before then is held and uploaded right after.
  const openBugCreate = () => {
    setBugEdit({
      value: null,
      isNew: true,
      isCustom: true,
      customId: null,
      frInsectId: null,
      name: "",
      defaultName: "",
      imageUrl: null,
      defaultImageUrl: null,
      isCustomized: false,
      pendingFile: null,
    });
  };

  // Put a custom bug's current option into every loaded copy of the catalog,
  // adding it if it is new.
  const applyCustomInsect = option => {
    const key = insectKeyFor(option);
    const upsert = list => {
      const idx = list.findIndex(i => insectKeyFor(i) === key);
      if (idx === -1) return [...list, option];
      return list.map((i, n) => (n === idx ? { ...i, ...option } : i));
    };
    setBugSettings(prev =>
      prev
        ? { ...prev, insects: upsert(prev.insects ?? []) }
        : { insects: [option], service_types: [] }
    );
    setCoverageInsects(prev =>
      prev.some(i => insectKeyFor(i) === key) ? upsert(prev) : prev
    );
    setSeasonInsects(prev =>
      prev.some(i => insectKeyFor(i) === key) ? upsert(prev) : prev
    );
  };

  // Take a deleted custom bug out of the catalog and off every list and
  // window it was on, matching what the API removed.
  const removeCustomInsect = key => {
    const drop = list => list.filter(i => insectKeyFor(i) !== key);
    setBugSettings(prev =>
      prev ? { ...prev, insects: drop(prev.insects ?? []) } : prev
    );
    setCoverageInsects(drop);
    setSeasonInsects(drop);
    const dropFromService = entry => ({
      ...entry,
      includedBugs: entry.includedBugs.filter(v => v !== key),
      excludedBugs: entry.excludedBugs.filter(v => v !== key),
    });
    setServiceBugs(prev => prev.map(dropFromService));
    setServiceBugsSnapshot(prev => prev.map(dropFromService));
    const dropFromRow = row => ({
      ...row,
      seasons: row.seasons.map(season => ({
        ...season,
        pests: season.pests.filter(v => v !== key),
      })),
    });
    setSeasonRows(prev => prev.map(dropFromRow));
    setSeasonRowsSnapshot(prev => prev.map(dropFromRow));
  };

  // Lay an override state ({ fr_insect_id, name, image_url } with nulls for
  // "no override") over every loaded copy of that insect.
  const applyInsectOverride = override => {
    const patch = insect => {
      if (String(insect.fr_insect_id) !== String(override.fr_insect_id)) {
        return insect;
      }
      const defaultName = insect.default_name ?? insect.name;
      const defaultImageUrl = insect.default_image_url ?? insect.image_url;
      return {
        ...insect,
        default_name: defaultName,
        default_image_url: defaultImageUrl,
        name: override.name || defaultName,
        image_url: override.image_url || defaultImageUrl,
        is_customized: !!(override.name || override.image_url),
      };
    };
    setBugSettings(prev =>
      prev ? { ...prev, insects: (prev.insects ?? []).map(patch) } : prev
    );
    setCoverageInsects(prev => prev.map(patch));
    setSeasonInsects(prev => prev.map(patch));
  };

  const bugOverrideRequest = async (path, init, base = "insects") => {
    const jwt = await getVerifiedJWT();
    if (!jwt) throw new Error("Authentication failed");
    const response = await fetch(
      getApiEndpoint(`/v1/welcome-survey/bug-settings/${base}/${path}`),
      {
        ...init,
        headers: { Authorization: `Bearer ${jwt}`, ...(init.headers ?? {}) },
      }
    );
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.message || "Failed to update bug");
    }
    const result = await response.json();
    return result?.data ?? result;
  };

  // Save the name. A name that matches FieldRoutes' is sent empty, which
  // clears the override rather than storing a copy of the default.
  const saveBugEdit = async () => {
    if (!bugEdit) return;
    const trimmed = bugEdit.name.trim();
    if (!trimmed) {
      showError("Give the bug a name");
      return;
    }
    setBugEditSaving(true);
    try {
      if (bugEdit.isCustom) {
        // Company-added bug: create or rename, then the held photo, if any.
        const json = {
          method: bugEdit.isNew ? "POST" : "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: trimmed }),
        };
        let option = await bugOverrideRequest(
          bugEdit.isNew ? "" : bugEdit.customId,
          json,
          "custom-insects"
        );
        if (bugEdit.pendingFile) {
          const formData = new FormData();
          formData.append("image", bugEdit.pendingFile);
          option = await bugOverrideRequest(
            `${option.custom_insect_id}/image`,
            { method: "POST", body: formData },
            "custom-insects"
          );
        }
        applyCustomInsect(option);
        setBugEdit(null);
        showSuccess(bugEdit.isNew ? "Bug added" : "Bug updated");
        return;
      }
      const override = await bugOverrideRequest(bugEdit.frInsectId, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: trimmed === bugEdit.defaultName ? "" : trimmed,
        }),
      });
      applyInsectOverride(override);
      setBugEdit(null);
      showSuccess("Bug updated");
    } catch (err) {
      showError(err.message || "Failed to update bug");
    } finally {
      setBugEditSaving(false);
    }
  };

  // The photo uploads as soon as it's picked; the modal shows the result. A
  // bug that doesn't exist yet holds the file until Save creates it.
  const uploadBugImage = async file => {
    if (!bugEdit || !file) return;
    if (bugEdit.isNew) {
      const previewUrl = URL.createObjectURL(file);
      setBugEdit(prev =>
        prev ? { ...prev, pendingFile: file, imageUrl: previewUrl } : prev
      );
      return;
    }
    setBugEditUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      if (bugEdit.isCustom) {
        const option = await bugOverrideRequest(
          `${bugEdit.customId}/image`,
          { method: "POST", body: formData },
          "custom-insects"
        );
        applyCustomInsect(option);
        setBugEdit(prev =>
          prev ? { ...prev, imageUrl: option.image_url } : prev
        );
        showSuccess("Photo updated");
        return;
      }
      const override = await bugOverrideRequest(`${bugEdit.frInsectId}/image`, {
        method: "POST",
        body: formData,
      });
      applyInsectOverride(override);
      setBugEdit(prev =>
        prev
          ? { ...prev, imageUrl: override.image_url, isCustomized: true }
          : prev
      );
      showSuccess("Photo updated");
    } catch (err) {
      showError(err.message || "Failed to upload photo");
    } finally {
      setBugEditUploading(false);
    }
  };

  // Remove a company-added bug, and with it every list and window it was on.
  const deleteBugEdit = async () => {
    if (!bugEdit?.isCustom || bugEdit.isNew) return;
    const ok = window.confirm(
      `Delete "${bugEdit.name.trim() || "this bug"}"? It comes off every coverage list and seasonal window it is on.`
    );
    if (!ok) return;
    setBugEditSaving(true);
    try {
      await bugOverrideRequest(
        bugEdit.customId,
        { method: "DELETE" },
        "custom-insects"
      );
      removeCustomInsect(bugEdit.value);
      setBugEdit(null);
      showSuccess("Bug deleted");
    } catch (err) {
      showError(err.message || "Failed to delete bug");
    } finally {
      setBugEditSaving(false);
    }
  };

  // Back to the FieldRoutes name and photo.
  const resetBugEdit = async () => {
    if (!bugEdit) return;
    setBugEditSaving(true);
    try {
      const override = await bugOverrideRequest(bugEdit.frInsectId, {
        method: "DELETE",
      });
      applyInsectOverride(override);
      setBugEdit(null);
      showSuccess("Bug reset to FieldRoutes");
    } catch (err) {
      showError(err.message || "Failed to reset bug");
    } finally {
      setBugEditSaving(false);
    }
  };

  // Upload a newly selected image to its dedicated endpoint and reflect the
  // returned URL in the form state. The upload endpoint stores the URL itself,
  // so there's nothing further to save.
  const handleImageUpload = async (field, file) => {
    const config = IMAGE_UPLOADS[field];
    if (!config) return;

    setUploadingField(field);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(getApiEndpoint(config.endpoint), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      const result = await response.json();
      const data = result?.data ?? result;
      const newUrl =
        pickImageUrl(data, config.responseKey) ||
        pickImageUrl(result, config.responseKey);

      if (!newUrl) {
        throw new Error("Upload succeeded but no image URL was returned");
      }

      setSettings(prev => ({ ...prev, [field]: newUrl }));
      showSuccess("Image updated");
    } catch (err) {
      showError(err.message || "Failed to upload image");
    } finally {
      setUploadingField(null);
    }
  };

  // Clear an image: the matching remove_* flag deletes it server-side.
  const handleImageRemove = async field => {
    const config = IMAGE_UPLOADS[field];
    if (!config) return;
    const saved = await saveSettingsPatch(
      { [config.removeFlag]: true },
      "Image removed"
    );
    if (saved) setSettings(prev => ({ ...prev, [field]: "" }));
  };

  // Branch details, edited as a block from the email's "Your Branch" column.
  const handleEditBranch = () => {
    setBranchDraft({
      branchName: settings.branchName,
      branchAddress: settings.branchAddress,
      branchPhone: settings.branchPhone,
      branchEmail: settings.branchEmail,
    });
    setEditingBranch(true);
  };

  const handleSaveBranch = async () => {
    const saved = await saveSettingsPatch(
      {
        welcome_survey_branch_name: branchDraft.branchName,
        welcome_survey_branch_address: branchDraft.branchAddress,
        welcome_survey_branch_phone: branchDraft.branchPhone,
        welcome_survey_branch_email: branchDraft.branchEmail,
      },
      "Branch details saved"
    );
    if (saved) {
      setSettings(prev => ({ ...prev, ...branchDraft }));
      setEditingBranch(false);
    }
  };

  // Manager name, edited from the email's manager column (the photo has its
  // own control on the picture itself).
  const handleEditManager = () => {
    setManagerDraft(settings.managerName);
    setEditingManager(true);
  };

  const handleSaveManager = async () => {
    const saved = await saveSettingsPatch(
      { welcome_survey_manager_name: managerDraft },
      "Manager saved"
    );
    if (saved) {
      setSettings(prev => ({ ...prev, managerName: managerDraft }));
      setEditingManager(false);
    }
  };

  // Hero copy — the headline and paragraph at the top of the email. Saving
  // either one blank puts the template's own default back.
  const handleEditHero = () => {
    setHeroDraft({
      headline: settings.emailHeadline,
      body: settings.emailBody,
    });
    setEditingHero(true);
  };

  const handleSaveHero = async () => {
    const saved = await saveSettingsPatch(
      {
        welcome_survey_email_headline: heroDraft.headline,
        welcome_survey_email_body: heroDraft.body,
      },
      "Email copy saved"
    );
    if (saved) {
      setSettings(prev => ({
        ...prev,
        emailHeadline: heroDraft.headline,
        emailBody: heroDraft.body,
      }));
      setEditingHero(false);
    }
  };

  // Contract terms section (its own dedicated save call).
  const handleEditContract = () => {
    setContractSnapshot(settings.contractText);
    setIsEditingContract(true);
  };

  const handleCancelContract = () => {
    setSettings(prev => ({ ...prev, contractText: contractSnapshot }));
    setIsEditingContract(false);
  };

  const handleSaveContract = async () => {
    setSavingContract(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint("/v2/welcome-survey/contract-text"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            welcome_survey_contract_text: settings.contractText,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to save contract terms");
      }

      showSuccess("Contract terms saved");
      setContractSnapshot(settings.contractText);
      setIsEditingContract(false);
    } catch (err) {
      showError(err.message || "Failed to save contract terms");
    } finally {
      setSavingContract(false);
    }
  };

  // Discard unsaved service edits — coverage and visit windows both — and go
  // back to what was last saved.
  const handleCancelBugs = () => {
    setServiceBugs(serviceBugsSnapshot);
    setSeasonRows(seasonRowsSnapshot);
    setIsEditingBugs(false);
    setBugsDirty(false);
    setBugError(null);
    setSeasonsError(null);
  };

  // Save a service type as one thing: its coverage (pest lists + cancellation
  // fee) and its visit windows. Coverage goes up in a single call for every
  // service; windows are replaced one service at a time, and a service whose
  // windows are all gone is deleted outright.
  const handleSaveBugs = async () => {
    if (!bugsDirty) {
      setIsEditingBugs(false);
      return;
    }

    const problems = validateSeasonRows(seasonRows);
    if (problems.length > 0) {
      setSeasonsError({
        message: "Fix these before saving:",
        details: problems,
      });
      return;
    }

    setSaving(true);
    setBugError(null);
    setSeasonsError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      };

      const response = await fetch(
        getApiEndpoint("/v2/welcome-survey/bug-settings"),
        {
          method: "POST",
          headers,
          body: JSON.stringify(
            toBugPayload(serviceBugs, insectByValue, serviceNameByValue)
          ),
        }
      );
      if (!response.ok) {
        // Surface the validation message and any conflicting bugs inline.
        const data = await response.json().catch(() => ({}));
        setBugError({
          message: data.message || "Failed to save service types",
          conflicts: Array.isArray(data.conflicts) ? data.conflicts : [],
        });
        return;
      }

      // Windows only go up for services that are still configured and still
      // have a window; everything else is removed by name (which also covers a
      // service that was dropped entirely).
      const rows = seasonRows
        .filter(row => row.serviceId && row.seasons.length > 0)
        .map(row => ({
          ...row,
          serviceType: (
            serviceNameByValue[row.serviceId] ||
            row.serviceType ||
            ""
          ).trim(),
        }))
        .filter(row => row.serviceType);

      const keptIds = new Set(rows.map(row => row.serviceId));
      const removedNames = seasonRowsSnapshot
        .filter(row => !row.serviceId || !keptIds.has(row.serviceId))
        .map(row => row.serviceType)
        .filter(Boolean);

      for (const name of removedNames) {
        const removal = await fetch(
          getApiEndpoint(
            `/v2/welcome-survey/service-seasons/${encodeURIComponent(name)}`
          ),
          { method: "DELETE", headers }
        );
        if (!removal.ok) {
          throw new Error(`Failed to remove the visit windows for ${name}`);
        }
      }

      for (const row of rows) {
        const saved = await fetch(
          getApiEndpoint("/v2/welcome-survey/service-seasons"),
          {
            method: "PUT",
            headers,
            body: JSON.stringify(
              toSeasonPayload(row, insectByValue, serviceIdByName)
            ),
          }
        );
        if (!saved.ok) {
          const data = await saved.json().catch(() => ({}));
          throw new Error(
            data.message ||
              `Failed to save the visit windows for ${row.serviceType}`
          );
        }
      }

      showSuccess("Service saved");
      setServiceBugsSnapshot(serviceBugs);
      // Windows with nothing in them were just deleted server-side, so the
      // saved rows are the ones that survived.
      setSeasonRows(rows);
      setSeasonRowsSnapshot(rows);
      setIsEditingBugs(false);
      setBugsDirty(false);
    } catch (err) {
      setSeasonsError({
        message: err.message || "Failed to save the service",
        details: [],
      });
    } finally {
      setSaving(false);
    }
  };

  // Reps: open the add/edit modal (no entry means add).
  const openWhitelistForm = (entry = null) => {
    setWhitelistEditing(entry);
    setWhitelistDraft({
      firstName: entry?.first_name ?? "",
      lastName: entry?.last_name ?? "",
      employeeId: entry?.fr_employee_id ?? "",
      phone: entry?.phone ?? "",
      officeIds: (entry?.offices ?? [])
        .map(office => office.id)
        .filter(id => id != null),
    });
    setWhitelistFormError("");
    setWhitelistFormOpen(true);
  };

  // Toggle one of the offices the rep is assigned to.
  const toggleWhitelistOffice = (officeId, checked) =>
    setWhitelistDraft(prev => {
      const current = prev.officeIds ?? [];
      return {
        ...prev,
        officeIds: checked
          ? [...current, officeId]
          : current.filter(id => id !== officeId),
      };
    });

  const closeWhitelistForm = () => {
    setWhitelistFormOpen(false);
    setWhitelistEditing(null);
    setWhitelistFormError("");
  };

  // Create a new rep or update the one being edited, patching just that row
  // into local state rather than refetching the whole list.
  const handleSaveWhitelist = async () => {
    const firstName = whitelistDraft.firstName.trim();
    const lastName = whitelistDraft.lastName.trim();
    const phone = whitelistDraft.phone.trim();
    const editingId = whitelistEditing?.id;
    // NumberInput leaves an empty field as "", which Number() would read as 0.
    const employeeId =
      whitelistDraft.employeeId === "" || whitelistDraft.employeeId == null
        ? null
        : Number(whitelistDraft.employeeId);
    // The offices toggled on. Both endpoints take the rep's complete set:
    // on an edit, offices left off are unassigned.
    const officeIds = (whitelistDraft.officeIds ?? []).map(Number);

    if (!firstName || !lastName || !phone) {
      setWhitelistFormError("First name, last name and phone are required.");
      return;
    }

    // The employee id ties a new rep back to FieldRoutes; on an edit it's
    // fixed, so the field isn't shown and isn't sent.
    if (editingId == null && !Number.isInteger(employeeId)) {
      setWhitelistFormError("Employee ID is required.");
      return;
    }

    setWhitelistSaving(true);
    setWhitelistFormError("");
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      // Edits address the rep by id in the path; adds post to the collection.
      const response = await fetch(
        getApiEndpoint(
          editingId != null
            ? `/v2/welcome-survey/reps/${editingId}`
            : "/v2/welcome-survey/reps"
        ),
        {
          method: editingId != null ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(
            editingId != null
              ? {
                  first_name: firstName,
                  last_name: lastName,
                  phone,
                  office_ids: officeIds,
                }
              : {
                  first_name: firstName,
                  last_name: lastName,
                  fr_employee_id: employeeId,
                  phone,
                  office_ids: officeIds,
                }
          ),
        }
      );

      if (!response.ok) {
        throw new Error(
          editingId != null ? "Failed to update rep" : "Failed to add rep"
        );
      }

      // The save endpoint returns the full updated list, so use it directly.
      const result = await response.json().catch(() => ({}));
      const data = result?.data ?? result;
      if (Array.isArray(data)) setWhitelist(data);

      showSuccess(editingId != null ? "Rep updated" : "Rep added");
      closeWhitelistForm();
    } catch (err) {
      setWhitelistFormError(err.message || "Failed to save rep");
    } finally {
      setWhitelistSaving(false);
    }
  };

  // Delete the rep pending confirmation. The endpoint returns the full
  // updated list, so use it directly rather than refetching.
  const handleDeleteWhitelist = async () => {
    if (!whitelistDeleting) return;
    setWhitelistRemoving(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(
        getApiEndpoint(`/v2/welcome-survey/reps/${whitelistDeleting.id}`),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to delete rep");

      const result = await response.json().catch(() => ({}));
      const data = result?.data ?? result;
      if (Array.isArray(data)) setWhitelist(data);

      showSuccess("Rep deleted");
      setWhitelistDeleting(null);
    } catch (err) {
      showError(err.message || "Failed to delete rep");
    } finally {
      setWhitelistRemoving(false);
    }
  };

  // The roster the list shows: name or number matching the search box.
  const whitelistQuery = whitelistSearch.trim().toLowerCase();
  const visibleWhitelist = whitelistQuery
    ? whitelist.filter(entry =>
        `${repName(entry)} ${entry.phone ?? ""}`
          .toLowerCase()
          .includes(whitelistQuery)
      )
    : whitelist;

  // Shown on the email's logo band when the company hasn't uploaded one.
  const companyName = user?.company_name ?? user?.company?.name ?? "";

  return (
    <PageContent
      customHeader={
        <SubNav
          title="Welcome Survey"
          tabs={WELCOME_SURVEY_TABS}
          activeId="setup"
          ariaLabel="Welcome survey sections"
        />
      }
    >
      <div className="ws-setup">
        {loading ? (
          <Group justify="center" py="xl">
            <Loader size="lg" />
          </Group>
        ) : error ? (
          <Alert
            icon={<IconAlertCircle size={20} />}
            title="Error"
            color="red"
            variant="filled"
          >
            {error}
          </Alert>
        ) : (
          <div className="ws-setup-page">
            <div className="ws-setup-main">
              {/* Email content — the invitation itself, edited in place */}
              <Paper radius="md" withBorder className="ws-setup-toprow">
                <Stack gap="md">
                  <UnstyledButton
                    className="ws-setup-row"
                    onClick={() => setEmailSectionOpen(open => !open)}
                    aria-expanded={emailSectionOpen}
                  >
                    <span className="ws-setup-row-text">
                      <Title order={4} className="ws-setup-row-title">
                        Email Content Settings
                      </Title>
                      <Text className="ws-setup-sub">
                        The invitation your customers get. Click a pencil to
                        change that part of it.
                      </Text>
                    </span>
                    <IconChevronRight
                      size={18}
                      className={`ws-setup-row-chevron${
                        emailSectionOpen ? " ws-setup-row-chevron-open" : ""
                      }`}
                    />
                  </UnstyledButton>

                  <Collapse in={emailSectionOpen}>
                    <WelcomeSurveyEmailPreview
                      settings={settings}
                      accentColor={branding?.accent_color}
                      companyName={companyName}
                      headline={
                        settings.emailHeadline || branding?.email_headline
                      }
                      bodyText={settings.emailBody || branding?.email_body}
                      uploadingField={uploadingField}
                      onSelectImage={handleImageUpload}
                      onRemoveImage={handleImageRemove}
                      editingHero={editingHero}
                      heroDraft={heroDraft}
                      onHeroDraftChange={patch =>
                        setHeroDraft(prev => ({ ...prev, ...patch }))
                      }
                      onEditHero={handleEditHero}
                      onSaveHero={handleSaveHero}
                      onCancelHero={() => setEditingHero(false)}
                      editingBranch={editingBranch}
                      branchDraft={branchDraft}
                      onBranchDraftChange={patch =>
                        setBranchDraft(prev => ({ ...prev, ...patch }))
                      }
                      onEditBranch={handleEditBranch}
                      onSaveBranch={handleSaveBranch}
                      onCancelBranch={() => setEditingBranch(false)}
                      editingManager={editingManager}
                      managerDraft={managerDraft}
                      onManagerDraftChange={setManagerDraft}
                      onEditManager={handleEditManager}
                      onSaveManager={handleSaveManager}
                      onCancelManager={() => setEditingManager(false)}
                      saving={savingEmail}
                    />
                  </Collapse>
                </Stack>
              </Paper>

              {/* Services: what each plan covers, and when it's serviced */}
              <Paper radius="md" withBorder p="lg">
                <Stack gap="md">
                  <Group justify="space-between" align="flex-start">
                    <div>
                      <Title order={4}>Service Types</Title>
                      <Text size="sm" c="dimmed" mt={4}>
                        What each plan covers. Open one to edit it — the live
                        preview follows whichever one you have open.
                      </Text>
                    </div>
                    {!isEditingBugs && !loading && !error && (
                      <Tooltip label="Edit">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="lg"
                          aria-label="Edit service types"
                          onClick={handleEditBugs}
                        >
                          <IconPencil size={22} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>

                  {bugSettingsLoading || seasonsLoading ? (
                    <Group justify="center" py="md">
                      <Loader size="sm" />
                    </Group>
                  ) : serviceBugs.length === 0 ? (
                    <Text c="dimmed" size="sm">
                      {isEditingBugs
                        ? "No service types yet. Add one below."
                        : "No service types."}
                    </Text>
                  ) : (
                    /* One card per configured service type, laid out as a
                       grid the way the design has them rather than as a
                       stack of full-width rows. */
                    <div className="ws-setup-services">
                      {serviceBugs.map((entry, index) => {
                        const name = entry.isTemplate
                          ? SURVEY_TEMPLATE_NAME
                          : serviceNameByValue[entry.serviceId] ||
                            `Service ${index + 1}`;
                        const frequency = frequencyLabel(
                          (bugSettings?.service_types ?? []).find(
                            option =>
                              String(option.fr_service_id) === entry.serviceId
                          )?.frequency_days
                        );
                        const hasFee =
                          entry.cancellationFee !== "" &&
                          entry.cancellationFee != null;
                        return (
                          <UnstyledButton
                            key={index}
                            className="ws-setup-service"
                            onClick={() => setSelectedServiceIndex(index)}
                            aria-label={`Open ${name}`}
                          >
                            <span className="ws-setup-service-head">
                              <span className="ws-setup-service-name">
                                {name}
                              </span>
                              <IconChevronRight size={16} />
                            </span>
                            <span className="ws-setup-chips">
                              {frequency && (
                                <span className="ws-setup-chip">
                                  {frequency}
                                </span>
                              )}
                              <span
                                className={`ws-setup-chip${hasFee ? " ws-setup-chip-fee" : ""}`}
                              >
                                {hasFee
                                  ? `$${entry.cancellationFee} cancel fee`
                                  : "No cancel fee"}
                              </span>
                              <span className="ws-setup-chip">
                                {entry.includedBugs.length} target bug
                                {entry.includedBugs.length === 1 ? "" : "s"}
                              </span>
                              <span className="ws-setup-chip">
                                {entry.excludedBugs.length} not included
                              </span>
                            </span>
                          </UnstyledButton>
                        );
                      })}
                    </div>
                  )}

                  {bugError && (
                    <Alert
                      icon={<IconAlertCircle size={20} />}
                      title="Couldn't save service types"
                      color="red"
                    >
                      <Text size="sm">{bugError.message}</Text>
                      {bugError.conflicts.length > 0 && (
                        <Stack gap={4} mt="xs">
                          {bugError.conflicts.map((conflict, i) => (
                            <Text key={i} size="sm">
                              <strong>{conflict.service_type}:</strong>{" "}
                              {(conflict.bugs || []).join(", ")}
                            </Text>
                          ))}
                        </Stack>
                      )}
                    </Alert>
                  )}

                  {isEditingBugs && (
                    <>
                      <Button
                        variant="light"
                        leftSection={<IconPlus size={16} />}
                        onClick={addServiceBug}
                        style={{ alignSelf: "flex-start" }}
                      >
                        Add service type
                      </Button>
                      <Group justify="flex-end" mt="md" align="center">
                        <Button
                          variant="default"
                          onClick={handleCancelBugs}
                          disabled={saving}
                        >
                          Cancel
                        </Button>
                        <Button onClick={handleSaveBugs} loading={saving}>
                          Save
                        </Button>
                      </Group>
                    </>
                  )}
                </Stack>
              </Paper>
              {/* Survey Setup: the open service type's fields, with the live
              {/* Survey Setup: the open service type's fields and the live
                  preview of them, in one card rather than two. */}
              <Paper radius="md" withBorder className="ws-setup-editor-main">
                <div className="ws-setup-editor-head">
                  <Title order={4}>Survey Setup</Title>
                  {selectedService && isEditingBugs && (
                    <div className="ws-setup-actions">
                      {/* Puts back the snapshot taken when editing began, so it
                          drops every unsaved change to this service and its
                          windows, not just the field last touched. */}
                      <UnstyledButton
                        className="ws-setup-discard"
                        onClick={handleCancelBugs}
                        disabled={saving}
                      >
                        Discard
                      </UnstyledButton>
                      <UnstyledButton
                        className="ws-setup-save"
                        onClick={handleSaveBugs}
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "Save"}
                      </UnstyledButton>
                    </div>
                  )}
                </div>
                <div className="ws-setup-editor-body">
                  <div className="ws-setup-editor-form">
                    {selectedService ? (
                      /* Detail: one service type's fields. Both stacks fill
                         the cell so the columns inside reach the phone's
                         height rather than stopping at their content. */
                      <Stack gap="md" className="ws-setup-detail">
                        <Stack gap="sm" className="ws-setup-detail">
                          {/* Two columns, as the design splits them: the
                            service's own fields and its visit windows on
                            the left, everything about bug coverage on the
                            right. */}
                          <div className="ws-setup-cols">
                            <div className="ws-setup-col ws-setup-col-left">
                              {selectedService.isTemplate ? (
                                /* The template is not one of the office's services, so there is
                                   nothing to pick — it is named, not chosen. */
                                <div className="ws-setup-template-name">
                                  <span className="ws-setup-col-title">
                                    Service
                                  </span>
                                  <span className="ws-setup-template-value">
                                    {SURVEY_TEMPLATE_NAME}
                                  </span>
                                </div>
                              ) : (
                                <Select
                                  label="Service"
                                  classNames={{ label: "ws-setup-col-title" }}
                                  placeholder="Select a service"
                                  // Only offer services not already configured under
                                  // another entry (this one keeps its own selection).
                                  data={serviceTypeOptions.filter(
                                    option =>
                                      option.value ===
                                        selectedService.serviceId ||
                                      !serviceBugs.some(
                                        (other, i) =>
                                          i !== selectedServiceIndex &&
                                          other.serviceId === option.value
                                      )
                                  )}
                                  value={selectedService.serviceId}
                                  onChange={value =>
                                    updateServiceBug(selectedServiceIndex, {
                                      serviceId: value,
                                    })
                                  }
                                  disabled={!isEditingBugs}
                                  searchable
                                />
                              )}

                              <NumberInput
                                label="Cancel Fee"
                                placeholder="0.00"
                                value={selectedService.cancellationFee}
                                onChange={value =>
                                  updateServiceBug(selectedServiceIndex, {
                                    cancellationFee: value,
                                  })
                                }
                                disabled={!isEditingBugs}
                                prefix="$"
                                min={0}
                                decimalScale={2}
                                thousandSeparator=","
                                allowNegative={false}
                                hideControls
                              />
                              {/* Terms: the design keeps the contract copy inside Survey Setup,
                                  under the service it applies to, rather than in a card of its
                                  own. The text is company-wide — editing it here changes it for
                                  every service type. */}
                              <div className="ws-setup-terms">
                                <div className="ws-setup-terms-head">
                                  <span className="ws-setup-terms-label">
                                    Terms
                                  </span>
                                  {!isEditingContract && !loading && !error && (
                                    <Tooltip label="Edit">
                                      <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        size="sm"
                                        aria-label="Edit contract terms"
                                        onClick={handleEditContract}
                                      >
                                        <IconPencil size={16} />
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                </div>

                                <ContractTextEditor
                                  value={settings.contractText}
                                  editable={isEditingContract}
                                  onChange={html =>
                                    setSettings(prev => ({
                                      ...prev,
                                      contractText: html,
                                    }))
                                  }
                                />

                                {isEditingContract && (
                                  <Group justify="flex-end">
                                    <Button
                                      variant="default"
                                      onClick={handleCancelContract}
                                      disabled={savingContract}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      onClick={handleSaveContract}
                                      loading={savingContract}
                                    >
                                      Save
                                    </Button>
                                  </Group>
                                )}
                              </div>

                              {/* Visit windows for this service only. The
                                  cadence is FieldRoutes' own figure for the
                                  service type — it isn't editable here, it
                                  says what the plan the customer bought
                                  actually runs. */}
                              <div className="ws-setup-window">
                                <div className="ws-setup-window-head">
                                  <span className="ws-setup-window-name">
                                    Service Window
                                  </span>
                                  {selectedWindowRange && (
                                    <span className="ws-setup-window-range">
                                      {selectedWindowRange}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* One card per visit the plan runs, numbered.
                                  The count comes from the service type's
                                  visits-a-year, so a plan with four visits
                                  shows four slots whether or not each has a
                                  window configured yet. */}
                              <Stack gap="sm">
                                {visitSlots.map(seasonIndex => {
                                  const season =
                                    selectedServiceSeasons[seasonIndex];
                                  if (!season) {
                                    return (
                                      <Paper
                                        key={`slot-${seasonIndex}`}
                                        withBorder
                                        radius="sm"
                                        p="sm"
                                        className="ws-setup-visit ws-setup-visit-empty"
                                      >
                                        <Group justify="space-between">
                                          <Text size="sm" fw={600}>
                                            Visit {seasonIndex + 1}
                                          </Text>
                                          {isEditingBugs &&
                                            selectedService.serviceId && (
                                              <Button
                                                variant="subtle"
                                                size="compact-xs"
                                                onClick={() =>
                                                  fillSeasonSlot(
                                                    selectedService.serviceId,
                                                    seasonIndex
                                                  )
                                                }
                                              >
                                                Set up
                                              </Button>
                                            )}
                                        </Group>
                                      </Paper>
                                    );
                                  }
                                  const wraps =
                                    season.startMonth &&
                                    season.endMonth &&
                                    Number(season.startMonth) >
                                      Number(season.endMonth);

                                  return (
                                    <Paper
                                      key={seasonIndex}
                                      withBorder
                                      radius="sm"
                                      p="sm"
                                      className="ws-setup-visit-filled"
                                    >
                                      <Group justify="space-between" mb="xs">
                                        <Text size="sm" fw={600}>
                                          {season.name ||
                                            `Visit ${seasonIndex + 1}`}
                                        </Text>
                                        {isEditingBugs && (
                                          <ActionIcon
                                            variant="subtle"
                                            color="red"
                                            size="sm"
                                            aria-label="Remove window"
                                            onClick={() =>
                                              removeSeason(
                                                selectedService.serviceId,
                                                seasonIndex
                                              )
                                            }
                                          >
                                            <IconTrash size={16} />
                                          </ActionIcon>
                                        )}
                                      </Group>

                                      <Stack gap="xs">
                                        <Group grow align="flex-start">
                                          <Select
                                            label="From"
                                            placeholder="Month"
                                            data={MONTH_OPTIONS}
                                            value={season.startMonth}
                                            onChange={value =>
                                              updateSeason(
                                                selectedService.serviceId,
                                                seasonIndex,
                                                { startMonth: value }
                                              )
                                            }
                                            disabled={!isEditingBugs}
                                          />
                                          <Select
                                            label="To"
                                            placeholder="Month"
                                            data={MONTH_OPTIONS}
                                            value={season.endMonth}
                                            onChange={value =>
                                              updateSeason(
                                                selectedService.serviceId,
                                                seasonIndex,
                                                { endMonth: value }
                                              )
                                            }
                                            disabled={!isEditingBugs}
                                          />
                                        </Group>
                                        {wraps && (
                                          <Text size="xs" c="dimmed">
                                            Runs across the year end:{" "}
                                            {
                                              MONTH_LABELS[
                                                Number(season.startMonth) - 1
                                              ]
                                            }{" "}
                                            through{" "}
                                            {
                                              MONTH_LABELS[
                                                Number(season.endMonth) - 1
                                              ]
                                            }
                                            .
                                          </Text>
                                        )}
                                        <TextInput
                                          label="Visit name"
                                          placeholder="Winter Service"
                                          value={season.name}
                                          maxLength={120}
                                          onChange={event =>
                                            updateSeason(
                                              selectedService.serviceId,
                                              seasonIndex,
                                              {
                                                name: event.currentTarget.value,
                                              }
                                            )
                                          }
                                          disabled={!isEditingBugs}
                                        />
                                        <MultiSelect
                                          label="Target bugs"
                                          placeholder="Select bugs"
                                          data={seasonPestOptions}
                                          value={season.pests}
                                          onChange={value =>
                                            updateSeason(
                                              selectedService.serviceId,
                                              seasonIndex,
                                              { pests: value }
                                            )
                                          }
                                          renderOption={renderInsectOption}
                                          disabled={!isEditingBugs}
                                          searchable
                                          clearable
                                        />
                                      </Stack>
                                    </Paper>
                                  );
                                })}
                              </Stack>

                              {seasonsError && (
                                <Alert
                                  icon={<IconAlertCircle size={20} />}
                                  title="Couldn't save this service"
                                  color="red"
                                >
                                  <Text size="sm">{seasonsError.message}</Text>
                                  {seasonsError.details.length > 0 && (
                                    <Stack gap={4} mt="xs">
                                      {seasonsError.details.map((detail, i) => (
                                        <Text key={i} size="sm">
                                          {detail}
                                        </Text>
                                      ))}
                                    </Stack>
                                  )}
                                </Alert>
                              )}
                            </div>

                            <div className="ws-setup-col ws-setup-col-bugs">
                              {/* No heading: the column starts at the phone's
                                  top edge, with the coverage boxes level with
                                  it. The catalog is a card of its own and
                                  fills the space the coverage boxes leave, so
                                  it ends level with the phone too. */}

                              {/* What the plan covers, and what it explicitly
                                  doesn't. Both are filled from the index
                                  below rather than from a dropdown, so the
                                  whole catalog stays in view while choosing. */}
                              <BugBox
                                label="Target Pests"
                                tone="target"
                                values={selectedService.includedBugs}
                                nameFor={insectNameFor}
                                imageFor={insectImageByValue}
                                editable={isEditingBugs}
                                onRemove={value =>
                                  updateServiceBug(selectedServiceIndex, {
                                    includedBugs:
                                      selectedService.includedBugs.filter(
                                        v => v !== value
                                      ),
                                  })
                                }
                              />

                              <BugBox
                                label="Not Covered"
                                values={selectedService.excludedBugs}
                                nameFor={insectNameFor}
                                imageFor={insectImageByValue}
                                editable={isEditingBugs}
                                onRemove={value =>
                                  updateServiceBug(selectedServiceIndex, {
                                    excludedBugs:
                                      selectedService.excludedBugs.filter(
                                        v => v !== value
                                      ),
                                  })
                                }
                              />
                              <div className="ws-bug-card ws-bug-index">
                                <span className="ws-setup-terms-label">
                                  Bug Index
                                </span>
                                <label className="ws-setup-search">
                                  <IconSearch size={14} />
                                  <input
                                    type="search"
                                    value={bugSearch}
                                    onChange={event =>
                                      setBugSearch(event.target.value)
                                    }
                                    placeholder="Search bugs"
                                    aria-label="Search bugs"
                                  />
                                </label>

                                {visibleInsects.length === 0 ? (
                                  <div className="ws-bug-empty">
                                    {/* An empty catalog and a search that
                                        matched nothing are different problems;
                                        saying "no bugs match" for both hid the
                                        first one behind an empty quote. */}
                                    {insectOptions.length === 0
                                      ? "No bugs in the FieldRoutes catalog for this office yet."
                                      : `No bugs match "${bugSearch.trim()}".`}
                                  </div>
                                ) : (
                                  <div className="ws-bug-grid">
                                    {/* A bug of the company's own, beside
                                        the FieldRoutes catalog. */}
                                    <button
                                      type="button"
                                      className="ws-bug-tile ws-bug-add"
                                      onClick={openBugCreate}
                                      aria-label="Add a bug"
                                    >
                                      <span className="ws-bug-add-icon">
                                        <IconPlus size={18} />
                                      </span>
                                      <span className="ws-bug-name">
                                        Add bug
                                      </span>
                                    </button>
                                    {visibleInsects.map(option => {
                                      const included =
                                        selectedService.includedBugs.includes(
                                          option.value
                                        );
                                      const excluded =
                                        selectedService.excludedBugs.includes(
                                          option.value
                                        );
                                      return (
                                        <div
                                          className="ws-bug-tile"
                                          key={option.value}
                                        >
                                          {/* Opens the bug's own name and
                                              photo — separate from coverage
                                              editing, so always live. */}
                                          <button
                                            type="button"
                                            className="ws-bug-edit"
                                            aria-label={`Edit ${option.label}`}
                                            onClick={() =>
                                              openBugEdit(option.value)
                                            }
                                          >
                                            <IconPencil size={11} />
                                          </button>
                                          <Avatar
                                            src={
                                              insectImageByValue[option.value]
                                            }
                                            size={38}
                                            radius="xl"
                                          >
                                            {option.label?.charAt(0)}
                                          </Avatar>
                                          <span className="ws-bug-name">
                                            {option.label}
                                          </span>
                                          <span className="ws-bug-actions">
                                            {/* A bug belongs to one list or
                                                neither, so picking a side
                                                clears the other. */}
                                            <button
                                              type="button"
                                              className={`ws-bug-pick${excluded ? " ws-bug-pick-on-exclude" : ""}`}
                                              disabled={!isEditingBugs}
                                              aria-pressed={excluded}
                                              aria-label={`Mark ${option.label} not covered`}
                                              onClick={() =>
                                                setBugSide(option.value, "out")
                                              }
                                            >
                                              ✕
                                            </button>
                                            <button
                                              type="button"
                                              className={`ws-bug-pick${included ? " ws-bug-pick-on-target" : ""}`}
                                              disabled={!isEditingBugs}
                                              aria-pressed={included}
                                              aria-label={`Mark ${option.label} a target pest`}
                                              onClick={() =>
                                                setBugSide(option.value, "in")
                                              }
                                            >
                                              ✓
                                            </button>
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </Stack>
                      </Stack>
                    ) : (
                      <Text c="dimmed" size="sm">
                        Open a service type above to edit it.
                      </Text>
                    )}
                  </div>

                  <div className="survey-live-preview">
                    {/* Customers open the survey from a text, so it's shown on
                    an iPhone 16 laid out at the device's real point size and
                    scaled to fit the panel. */}
                    <Box
                      className="survey-live-preview-stage"
                      ref={phoneStageRef}
                      style={{ height: PHONE_OUTER_HEIGHT * phoneScale }}
                    >
                      <div
                        className="survey-live-preview-phone"
                        style={{ transform: `scale(${phoneScale})` }}
                      >
                        <div
                          className="survey-live-preview-island"
                          aria-hidden="true"
                        />
                        {/* The preview is a picture of what the customer
                            gets on their phone, and the survey itself only
                            ships a light theme — so it stays light whichever
                            theme the admin is on.

                            Three things carry a theme into here and all three
                            are pinned: data-theme puts the app's light tokens
                            back (colors.css declares them for this attribute
                            too), the nested provider re-emits Mantine's light
                            variables scoped to this element, and
                            data-mantine-color-scheme answers the component
                            rules keyed off that attribute. getRootElement is
                            neutered so the nested provider cannot flip the
                            <html> scheme the rest of the page is on. */}
                        <div
                          className="survey-live-preview-screen"
                          data-theme="light"
                          data-mantine-color-scheme="light"
                        >
                          <MantineProvider
                            theme={theme}
                            forceColorScheme="light"
                            cssVariablesSelector=".survey-live-preview-screen"
                            getRootElement={() => undefined}
                            withGlobalClasses={false}
                          >
                            <InfoVerificationPage
                              key={`${previewValue}|${previewStep}`}
                              previewData={previewData}
                              previewStep={previewStep}
                            />
                          </MantineProvider>
                        </div>
                        <div
                          className="survey-live-preview-home"
                          aria-hidden="true"
                        />
                      </div>
                    </Box>
                  </div>
                </div>
              </Paper>
            </div>

            <aside className="ws-setup-side">
              {/* The two numbers the survey runs on, side by side. Shares the
                  top-row height with the Email Content Settings card opposite,
                  so the two columns start on the same line. */}
              <Paper
                radius="md"
                withBorder
                className="ws-setup-toprow ws-setup-phones-card"
              >
                <div className="ws-setup-phones">
                  <div className="ws-setup-phone">
                    <span className="ws-setup-phone-label">
                      Inbound Phone Number
                    </span>
                    <span className="ws-setup-phone-value">
                      {settings.inboundPhone || "—"}
                    </span>
                  </div>
                  <div className="ws-setup-phone">
                    <span className="ws-setup-phone-label">
                      Outbound Phone Number
                    </span>
                    <span className="ws-setup-phone-value">
                      {settings.branchPhone || "—"}
                    </span>
                  </div>
                </div>
              </Paper>
              {/* Allowed Rep Phone List: name and number per row, the way the
                  design has it. Employee id and offices live in the edit form
                  rather than on the row. */}
              <Paper radius="md" withBorder className="ws-setup-reps">
                <div className="ws-setup-reps-head">
                  <div className="ws-setup-reps-heading">
                    <Title order={4}>Allowed Rep Phone List</Title>
                    <Text className="ws-setup-sub">
                      Reps whose phone numbers are allowed to generate the
                      welcome survey.
                    </Text>
                  </div>
                  <div className="ws-setup-reps-controls">
                    <label className="ws-setup-search">
                      <IconSearch size={14} />
                      <input
                        type="search"
                        value={whitelistSearch}
                        onChange={event =>
                          setWhitelistSearch(event.target.value)
                        }
                        placeholder="Search reps by name or phone"
                        aria-label="Search reps by name or phone"
                      />
                    </label>
                    <UnstyledButton
                      className="ws-setup-add"
                      onClick={() => openWhitelistForm()}
                    >
                      + Add entry
                    </UnstyledButton>
                  </div>
                </div>

                <div className="ws-setup-reps-list">
                  {whitelistLoading ? (
                    <Group justify="center" py="md">
                      <Loader size="sm" />
                    </Group>
                  ) : visibleWhitelist.length === 0 ? (
                    <div className="ws-setup-reps-empty">
                      {whitelist.length === 0
                        ? "No reps yet. Add one above."
                        : `No reps match "${whitelistSearch.trim()}".`}
                    </div>
                  ) : (
                    visibleWhitelist.map((entry, index) => (
                      <div
                        className="ws-setup-rep"
                        key={entry.id ?? index}
                        role="button"
                        tabIndex={0}
                        onClick={() => openWhitelistForm(entry)}
                        onKeyDown={event => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openWhitelistForm(entry);
                          }
                        }}
                      >
                        <span className="ws-setup-rep-name">
                          {repName(entry) || "—"}
                        </span>
                        <span className="ws-setup-rep-phone">
                          {entry.phone || "—"}
                        </span>
                        <span className="ws-setup-rep-actions">
                          <Tooltip label="Delete">
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              size="sm"
                              aria-label={`Delete ${repName(entry) || "rep"}`}
                              onClick={event => {
                                event.stopPropagation();
                                setWhitelistDeleting(entry);
                              }}
                            >
                              <IconTrash size={15} />
                            </ActionIcon>
                          </Tooltip>
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </Paper>
            </aside>
          </div>
        )}

        {/* Edit a catalog bug's name and photo */}
        <Modal
          opened={bugEdit != null}
          onClose={() => setBugEdit(null)}
          title={bugEdit?.isNew ? "Add bug" : "Edit bug"}
          centered
        >
          {bugEdit && (
            <Stack gap="md">
              <Group align="center" gap="md" wrap="nowrap">
                <Avatar src={bugEdit.imageUrl} size={72} radius="xl">
                  {bugEdit.name.charAt(0)}
                </Avatar>
                <Stack gap={4}>
                  <FileButton
                    onChange={uploadBugImage}
                    accept="image/png,image/jpeg,image/webp"
                  >
                    {props => (
                      <Button
                        {...props}
                        variant="default"
                        size="xs"
                        loading={bugEditUploading}
                      >
                        Upload photo
                      </Button>
                    )}
                  </FileButton>
                  <Text size="xs" c="dimmed">
                    {bugEdit.isCustom
                      ? bugEdit.imageUrl
                        ? "Your photo."
                        : "No photo yet — a lettered circle shows instead."
                      : bugEdit.imageUrl &&
                          bugEdit.imageUrl !== bugEdit.defaultImageUrl
                        ? "Your photo. Reset to go back to FieldRoutes'."
                        : bugEdit.defaultImageUrl
                          ? "The FieldRoutes photo."
                          : "FieldRoutes has no photo for this bug."}
                  </Text>
                </Stack>
              </Group>

              <TextInput
                label="Name"
                value={bugEdit.name}
                placeholder={bugEdit.defaultName}
                description={
                  !bugEdit.isCustom &&
                  bugEdit.defaultName &&
                  bugEdit.name !== bugEdit.defaultName
                    ? `FieldRoutes calls it "${bugEdit.defaultName}".`
                    : undefined
                }
                maxLength={120}
                onChange={e => {
                  const name = e.currentTarget.value;
                  setBugEdit(prev => (prev ? { ...prev, name } : prev));
                }}
                onKeyDown={e => {
                  if (e.key === "Enter") saveBugEdit();
                }}
                data-autofocus
              />

              <Group justify="space-between">
                {bugEdit.isCustom && !bugEdit.isNew ? (
                  <Button
                    variant="subtle"
                    color="red"
                    size="xs"
                    onClick={deleteBugEdit}
                    disabled={bugEditSaving || bugEditUploading}
                  >
                    Delete bug
                  </Button>
                ) : bugEdit.isCustomized ? (
                  <Button
                    variant="subtle"
                    color="gray"
                    size="xs"
                    onClick={resetBugEdit}
                    disabled={bugEditSaving || bugEditUploading}
                  >
                    Reset to FieldRoutes
                  </Button>
                ) : (
                  <span />
                )}
                <Group gap="xs">
                  <Button
                    variant="default"
                    onClick={() => setBugEdit(null)}
                    disabled={bugEditSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveBugEdit}
                    loading={bugEditSaving}
                    disabled={bugEditUploading}
                  >
                    Save
                  </Button>
                </Group>
              </Group>
            </Stack>
          )}
        </Modal>

        {/* Add / edit a rep */}
        <Modal
          opened={whitelistFormOpen}
          onClose={closeWhitelistForm}
          title={whitelistEditing ? "Edit Rep" : "Add Rep"}
          size="md"
        >
          <Stack gap="md">
            {whitelistFormError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
              >
                {whitelistFormError}
              </Alert>
            )}

            <TextInput
              label="First Name"
              placeholder="Enter first name"
              value={whitelistDraft.firstName}
              onChange={event =>
                setWhitelistDraft(prev => ({
                  ...prev,
                  firstName: event.currentTarget.value,
                }))
              }
              required
              data-autofocus
            />
            <TextInput
              label="Last Name"
              placeholder="Enter last name"
              value={whitelistDraft.lastName}
              onChange={event =>
                setWhitelistDraft(prev => ({
                  ...prev,
                  lastName: event.currentTarget.value,
                }))
              }
              required
            />
            {/* Set once, when the rep is first added. */}
            {!whitelistEditing && (
              <NumberInput
                label="Employee ID"
                placeholder="Enter FieldRoutes employee ID"
                value={whitelistDraft.employeeId}
                onChange={value =>
                  setWhitelistDraft(prev => ({ ...prev, employeeId: value }))
                }
                allowDecimal={false}
                allowNegative={false}
                required
              />
            )}
            <TextInput
              label="Phone"
              placeholder="15555555555"
              value={whitelistDraft.phone}
              onChange={event =>
                setWhitelistDraft(prev => ({
                  ...prev,
                  // Digits only, capped at 11 (country code + 10-digit number).
                  phone: event.currentTarget.value
                    .replace(/\D/g, "")
                    .slice(0, 11),
                }))
              }
              inputMode="numeric"
              maxLength={11}
              required
            />
            <Input.Wrapper
              label="Offices"
              description="The offices this rep belongs to."
            >
              {offices.length === 0 ? (
                <Text size="sm" c="dimmed" mt={6}>
                  No offices yet. Add one on the FieldRoutes Setup page.
                </Text>
              ) : (
                <Stack gap="xs" mt={8}>
                  {offices.map(office => (
                    <Switch
                      key={office.id}
                      label={office.name || `Office ${office.id}`}
                      size="sm"
                      checked={(whitelistDraft.officeIds ?? []).includes(
                        office.id
                      )}
                      onChange={event =>
                        toggleWhitelistOffice(
                          office.id,
                          event.currentTarget.checked
                        )
                      }
                    />
                  ))}
                </Stack>
              )}
            </Input.Wrapper>

            <Group justify="flex-end" gap="sm">
              <Button
                variant="default"
                onClick={closeWhitelistForm}
                disabled={whitelistSaving}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveWhitelist} loading={whitelistSaving}>
                {whitelistEditing ? "Save" : "Add"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Confirm deleting a rep */}
        <Modal
          opened={whitelistDeleting != null}
          onClose={() => setWhitelistDeleting(null)}
          title="Delete Rep"
          size="md"
        >
          <Stack gap="md">
            <Text size="sm">
              Delete{" "}
              <Text span fw={600}>
                {repName(whitelistDeleting) || "this rep"}
              </Text>
              {whitelistDeleting?.phone ? ` (${whitelistDeleting.phone})` : ""}?
              This can&apos;t be undone.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="default"
                onClick={() => setWhitelistDeleting(null)}
                disabled={whitelistRemoving}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleDeleteWhitelist}
                loading={whitelistRemoving}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </div>
    </PageContent>
  );
}

export default WelcomeSurveySettingsPage;
